import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { sparesByCompanyID, listSpares } from "graphql/queries";
import { updateSpare } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { SpareCreateForm, SpareUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  getCompanyName,
  getEquipmentName,
  getServiceOrderCode,
} from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import { globalStrings } from "config/translations/es";
import SpareParameters from "./parameters/SpareParameters";

const Spares = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete, canReadParams] = usePermissions([
    "spare:create",
    "spare:update",
    "spare:delete",
    "spare:read-param",
  ]);
  const [spares, setSpares] = useState([]);
  const [sparesLoading, setSparesLoading] = useState(false);
  const [spareDetails, setSpareDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    spareId: null,
    spareName: null,
  });
  const [modalSpare, setModalSpare] = useState({
    visible: false,
    spareId: null,
  });
  const [modalParams, setModalParams] = useState({
    visible: false,
    spareId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      spares.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        serviceOrderName: !item.hasOwnProperty("serviceOrderName") ? (
          <Loader />
        ) : (
          item.serviceOrderName
        ),
        equipmentName: !item.hasOwnProperty("equipmentName") ? (
          <Loader />
        ) : (
          item.equipmentName
        ),
        actions: (
          <>
            <ActionIcon
              name={
                spareDetails?.id === item.id
                  ? strings.spares.hide
                  : strings.spares.show
              }
              icon={
                spareDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                spareDetails?.id === item.id
                  ? setSpareDetails(null)
                  : setSpareDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.spares.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canReadParams && (
              <ActionIcon
                name={strings.spares.params}
                icon="Tune"
                color="primary"
                onClick={() => {
                  setModalParams({
                    visible: true,
                    spareId: item.id,
                    spareName: item.name,
                  });
                }}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.spares.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    spareId: item.id,
                    spareName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spares, spareDetails?.id]
  );

  const onNext = () => {
    fetchSpares(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchSpares(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchSpares(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalSpare({ visible: true, action, spareId: id });
  const onSuccessSpare = () => {
    setModalSpare({ visible: false });
    fetchSpares();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  async function fetchServiceOrderName(id) {
    try {
      const serviceOrderData = await API.graphql(
        graphqlOperation(getServiceOrderCode, {
          id,
        })
      );
      const serviceOrderItem = serviceOrderData.data.getServiceOrder;
      return serviceOrderItem?.code ?? "";
    } catch (err) {
      console.log("Error fetchServiceOrderName", err);
      return "";
    }
  }

  async function fetchEquipmentName(id) {
    try {
      const equipmentData = await API.graphql(
        graphqlOperation(getEquipmentName, {
          id,
        })
      );
      const equipmentItem = equipmentData.data.getEquipment;
      return equipmentItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchEquipmentName", err);
      return "";
    }
  }

  const getNamesBySpares = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);
      const serviceOrderName = await fetchServiceOrderName(item.serviceOrderID);
      const equipmentName = await fetchEquipmentName(item.equipmentID);

      setSpares((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
          serviceOrderName,
          equipmentName,
        };

        return updatedList;
      });
    });
  };

  async function fetchSpares(token, isNext = true, keywords) {
    let objOperation = {};
    let nameOperation = listSpares;
    let nameOperationStr = "listSpares";
    try {
      setSparesLoading(true);

      if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
        };
        nameOperation = sparesByCompanyID;
        nameOperationStr = "sparesByCompanyID";
      }

      const sparesData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                    {
                      location: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const sparesItems = sparesData.data[nameOperationStr].items;
      setSpares(sparesItems);
      getNamesBySpares(sparesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: sparesData.data[nameOperationStr].nextToken,
        };
      });
      setSparesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setSparesLoading(false);
    }
  }
  async function spareDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateSpare, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateSpare.id;
      setSpares(spares.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        spareId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchSpares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "20%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "serviceOrderName",
      label: strings.table.headers.serviceOrderName,
      _style: { width: "20%" },
    },
    {
      key: "equipmentName",
      label: strings.table.headers.equipmentName,
      _style: { width: "20%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.spares.addSpare}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {sparesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!spareDetails?.id} style={styles.collapseCard}>
          <CCard key={spareDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{spareDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {spareDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.location}:</b> {spareDetails?.location}
              </p>
              <p className="text-muted">
                <b>{strings.details.status}:</b>{" "}
                {spareDetails?.status &&
                  globalStrings.spareStatus[spareDetails?.status]}
              </p>
              <p className="text-muted">
                <b>{strings.details.serviceOrderName}:</b>{" "}
                {spareDetails?.serviceOrderName}
              </p>
              <p className="text-muted">
                <b>{strings.details.equipmentName}:</b>{" "}
                {spareDetails?.equipmentName}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(spareDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.spareName,
        })}
        onCancel={() => setModalDetails({ visible: false, spareId: null })}
        onConfirm={() => spareDelete(modalDetails.spareId)}
      />
      {modalSpare.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalSpare.visible}
          onClose={() => setModalSpare({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalSpare.action === "create"
                ? strings.modalSpare.addTitle
                : strings.modalSpare.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalSpare.action === "create" ? (
                <SpareCreateForm onSuccess={onSuccessSpare} />
              ) : (
                <SpareUpdateForm
                  spareId={modalSpare.spareId}
                  onSuccess={onSuccessSpare}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {modalParams.visible && (
        <SpareParameters
          spareID={modalParams.spareId}
          spareName={modalParams.spareName}
          visible={modalParams.visible}
          onClose={() => setModalParams({ visible: false })}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Repuestos",
    successDeleted: "Repuesto eliminado con éxito",
    spares: {
      addSpare: "Agregar Repuesto",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
    },
    responses: {
      errorFetch: "Error al consultar repuesto",
    },
    table: {
      headers: {
        name: "Nombre",
        companyName: "Empresa",
        status: "Estado",
        serviceOrderName: "Orden de Servicio",
        equipmentName: "Equipo",
        location: "Ubicación",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Repuesto",
      description: "Descripción",
      status: "Estado",
      location: "Ubicación",
      serviceOrderName: "Orden de Servicio",
      equipmentName: "Equipo",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el repuesto {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalSpare: {
      addTitle: "Agregar Repuesto",
      updateTitle: "Actualizar Repuesto",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Spares;
